import { languageFromPath, pathWithoutLanguage } from "@amtana/i18n";
import type { SupportedLanguage } from "@amtana/i18n";
import { lazy } from "react";
import type { ComponentType, LazyExoticComponent, ReactNode } from "react";

import type { ErrorCopy } from "./errors.ts";
import type {
  BrowserModule,
  FlagsSource,
  LayoutLoader,
  LendLoader,
  PermissionsSource,
  NavigationEntry,
  ScreenDeclaration,
} from "./web-module.ts";

// Merges every installed declaration into one route table, one layout table and
// one navigation list. A path, layout name or navigation id two modules claim is
// refused by name, before anything renders.

export class DeclarationClaimError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DeclarationClaimError";
  }
}

export type RouteEntry = {
  readonly owner: string;
  readonly screen: ScreenDeclaration;
  readonly view: LazyExoticComponent<ComponentType>;
  readonly segments: readonly string[];
};

export type LayoutEntry = LazyExoticComponent<ComponentType<{ readonly children: ReactNode }>>;

export type Installed = {
  readonly lends: ReadonlyMap<string, LendLoader>;
  readonly routes: readonly RouteEntry[];
  readonly layouts: ReadonlyMap<string, LayoutEntry>;
  /** The same layouts' own loaders, unwrapped from `lazy()`: a server render awaits one
   * directly, since `renderToString` cannot wait for a lazy component to resolve. */
  readonly layoutLoaders: ReadonlyMap<string, LayoutLoader>;
  readonly navigation: readonly NavigationEntry[];
  /** Every module's error copy, by code. */
  readonly errors: Readonly<Record<string, ErrorCopy>>;
  /** The one installed module's read of the active organization's flags, if any (ADR-006 §7). */
  readonly flagsSource?: FlagsSource;
  /** The one installed module's read of the session's permissions, if any (S12.01). */
  readonly permissionsSource?: PermissionsSource;
};

const segmentsOf = (path: string): string[] => path.split("/").filter(Boolean);

/** Static segments beat params; a longer static prefix beats a shorter one; "*" is last. */
function specificity(route: RouteEntry): number {
  if (route.screen.path === "*") return -1;
  return route.segments.reduce((score, segment) => score * 2 + (segment.startsWith(":") ? 0 : 1), 1);
}

export function install(modules: readonly BrowserModule[]): Installed {
  const routes: RouteEntry[] = [];
  const claimedPaths = new Map<string, string>();
  const layouts = new Map<string, LayoutEntry>();
  const layoutLoaders = new Map<string, LayoutLoader>();
  const layoutOwners = new Map<string, string>();
  const navigation = new Map<string, NavigationEntry & { owner: string }>();
  const lends = new Map<string, LendLoader>();
  const errors: Record<string, ErrorCopy> = {};
  const errorOwners = new Map<string, string>();
  let flagsSource: FlagsSource | undefined;
  let flagsSourceOwner: string | undefined;
  let permissionsSource: PermissionsSource | undefined;
  let permissionsSourceOwner: string | undefined;

  for (const module of modules) {
    for (const screen of Object.values(module.screens)) {
      const key =
        segmentsOf(screen.path)
          .map((s) => (s.startsWith(":") ? ":" : s))
          .join("/") || screen.path;
      const owner = claimedPaths.get(key);
      if (owner)
        throw new DeclarationClaimError(`Path "${screen.path}" is claimed by "${owner}" and "${module.id}".`);
      claimedPaths.set(key, module.id);
      routes.push({ owner: module.id, screen, view: lazy(screen.load), segments: segmentsOf(screen.path) });
    }
    for (const [name, layout] of Object.entries(module.layouts)) {
      const owner = layoutOwners.get(name);
      if (owner)
        throw new DeclarationClaimError(`Layout "${name}" is lent by "${owner}" and "${module.id}".`);
      layoutOwners.set(name, module.id);
      layouts.set(name, lazy(layout.load));
      layoutLoaders.set(name, layout.load);
    }
    for (const [id, lend] of Object.entries(module.lends)) {
      if (lends.has(id)) throw new DeclarationClaimError(`Lent component "${id}" is lent twice.`);
      lends.set(id, lend.load);
    }
    for (const [code, copy] of Object.entries(module.errors ?? {})) {
      const owner = errorOwners.get(code);
      if (owner)
        throw new DeclarationClaimError(`Error code "${code}" has copy from "${owner}" and "${module.id}".`);
      errorOwners.set(code, module.id);
      errors[code] = copy;
    }
    for (const entry of module.navigation) {
      const existing = navigation.get(entry.id);
      if (existing) {
        throw new DeclarationClaimError(
          `Navigation "${entry.id}" is declared by "${existing.owner}" and "${module.id}".`,
        );
      }
      navigation.set(entry.id, { ...entry, owner: module.id });
    }
    if (module.flagsSource) {
      if (flagsSourceOwner) {
        throw new DeclarationClaimError(
          `The active organization's flags are supplied by "${flagsSourceOwner}" and "${module.id}".`,
        );
      }
      flagsSource = module.flagsSource;
      flagsSourceOwner = module.id;
    }
    if (module.permissionsSource) {
      if (permissionsSourceOwner) {
        throw new DeclarationClaimError(
          `The session's permissions are supplied by "${permissionsSourceOwner}" and "${module.id}".`,
        );
      }
      permissionsSource = module.permissionsSource;
      permissionsSourceOwner = module.id;
    }
  }

  for (const route of routes) {
    if (route.screen.layout && !layouts.has(route.screen.layout)) {
      throw new DeclarationClaimError(
        `Screen "${route.screen.path}" of "${route.owner}" renders in layout "${route.screen.layout}", which no installed module lends.`,
      );
    }
  }

  return {
    lends,
    routes: routes.toSorted((a, b) => specificity(b) - specificity(a)),
    layouts,
    layoutLoaders,
    navigation: [...navigation.values()].toSorted((a, b) => a.order - b.order),
    errors,
    ...(flagsSource ? { flagsSource } : {}),
    ...(permissionsSource ? { permissionsSource } : {}),
  };
}

/** A public path and the languages it exists in; absent `languages` means every shipped one. */
export type PublicRoute = {
  readonly path: string;
  readonly languages?: readonly SupportedLanguage[];
};

/** Every installed path but the catch-all, in English, with its languages: what a build prerenders
 * and lists in the sitemap (ADR-027 point 6, ADR-029 point 2). A path with `:params` never
 * prerenders; none of the website's screens declare one today, so none is skipped. */
export function publicRoutes(modules: readonly BrowserModule[]): readonly PublicRoute[] {
  return install(modules)
    .routes.filter((route) => route.screen.path !== "*" && !route.segments.some((s) => s.startsWith(":")))
    .map(({ screen }) => ({
      path: screen.path,
      ...(screen.languages ? { languages: screen.languages } : {}),
    }));
}

/** `publicRoutes` with the paths alone. */
export function publicRoutePaths(modules: readonly BrowserModule[]): readonly string[] {
  return publicRoutes(modules).map((route) => route.path);
}

/** The language an address answers in: "/es/..." is Spanish, everything else English. */
export function addressLanguage(address: string): SupportedLanguage {
  return languageFromPath(address) === "es" ? "es" : "en";
}

export type Match = { readonly route: RouteEntry; readonly params: Readonly<Record<string, string>> };

export function match(
  routes: readonly RouteEntry[],
  path: string,
  language?: SupportedLanguage,
): Match | undefined {
  const parts = segmentsOf(path);
  for (const route of routes) {
    if (route.screen.path === "*") return { route, params: {} };
    if (route.segments.length !== parts.length) continue;
    // A page in one language has no address in the other (ADR-029 point 2).
    if (language && route.screen.languages && !route.screen.languages.includes(language)) continue;
    const params: Record<string, string> = {};
    const fits = route.segments.every((segment, index) => {
      const part = parts[index] ?? "";
      if (segment.startsWith(":")) {
        params[segment.slice(1)] = decodeURIComponent(part);
        return true;
      }
      return segment === part;
    });
    if (fits) return { route, params };
  }
  return undefined;
}

export const notFoundRouteOf = (routes: readonly RouteEntry[]): RouteEntry | undefined =>
  routes.find((route) => route.screen.path === "*");

/** The matched screen for an address, or the catch-all once nothing more specific fits: the same
 * resolution a server render and a client preload (before hydrating) both need, with no gate
 * (a website screen names no flag or permission; `create-ui.tsx`'s `Routes` still checks both
 * for the platform). */
export function resolveRoute(installed: Installed, address: string): Match | undefined {
  const found = match(installed.routes, pathWithoutLanguage(address), addressLanguage(address));
  const notFoundRoute = notFoundRouteOf(installed.routes);
  return found ?? (notFoundRoute ? { route: notFoundRoute, params: {} } : undefined);
}
